import React, { useEffect, useState, useCallback } from "react";
import { Button, Table, Spinner, Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import OmrService from "../../_services/OmrService";

interface OmrTestRow {
  Id: number;
  Name: string;
  TestDate: string;
  templateName: string;
  Status: string;
  scanCount: number;
  processedCount: number;
  NumberOfSets: number;
  TemplateId: number;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft: { bg: "#e2e3e5", color: "#383d41" },
  keys_entered: { bg: "#fff3cd", color: "var(--status-warning-text)" },
  scans_uploaded: { bg: "#cce5ff", color: "#004085" },
  processing: { bg: "#ffeeba", color: "var(--status-warning-text)" },
  review: { bg: "#f8d7da", color: "var(--status-danger-text)" },
  finalized: { bg: "#d4edda", color: "var(--status-success-text)" },
};

const OmrEvaluationPage: React.FC = () => {
  const history = useHistory();
  const [tests, setTests] = useState<OmrTestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [formName, setFormName] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formSets, setFormSets] = useState(4);

  const loadTests = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await OmrService.listTests();
      setTests(resp.data?.data || []);
    } catch (err: any) {
      setError(err.message || "Failed to load tests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const handleCreate = async () => {
    if (!formName.trim()) return;
    try {
      setCreating(true);
      // First ensure a default template exists, or create one
      let templateId: number;
      const templatesResp = await OmrService.listTemplates();
      const templates = templatesResp.data?.data || [];
      if (templates.length > 0) {
        templateId = templates[0].Id;
      } else {
        const createResp = await OmrService.createTemplate({ useDefault: true });
        templateId = createResp.data?.data?.id;
      }

      const resp = await OmrService.createTest({
        name: formName,
        templateId,
        testDate: formDate || null,
        numberOfSets: formSets,
        batchIds: [],
        sectionsConfig: [
          { name: "Physics", startQ: 1, endQ: 60, marksPerQ: 1, negativeMarks: 0 },
          { name: "Chemistry", startQ: 61, endQ: 120, marksPerQ: 1, negativeMarks: 0 },
          { name: "Mathematics", startQ: 121, endQ: 180, marksPerQ: 1, negativeMarks: 0 },
        ],
      });

      setShowCreate(false);
      setFormName("");
      setFormDate("");

      const omrTestId = resp.data?.data?.omrTestId;
      if (omrTestId) {
        history.push(`/omr-evaluation/${omrTestId}`);
      } else {
        loadTests();
      }
    } catch (err: any) {
      setError(err.message || "Failed to create test");
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadSheet = async (templateId: number, testName: string) => {
    try {
      const resp = await OmrService.generateSheetPdf(templateId || "default", { testName });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OMR_Sheet_${testName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError("Failed to download sheet: " + (err.message || ""));
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h4 style={{ margin: 0 }}>OMR Evaluation</h4>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          Create New Test
        </Button>
      </div>

      {error && (
        <div style={{ padding: "8px 12px", background: "var(--status-danger-bg)", color: "var(--status-danger-text)", borderRadius: 4, marginBottom: 12 }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: "right", border: "none", background: "none", cursor: "pointer" }}>x</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spinner animation="border" size="sm" /> Loading...</div>
      ) : tests.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)" }}>No OMR tests yet. Click "Create New Test" to get started.</div>
      ) : (
        <Table bordered hover size="sm" style={{ fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ background: "var(--surface-page)" }}>
              <th>Test Name</th>
              <th>Date</th>
              <th>Template</th>
              <th>Scans</th>
              <th>Status</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tests.map((t) => {
              const sc = STATUS_COLORS[t.Status] || STATUS_COLORS.draft;
              return (
                <tr key={t.Id} style={{ cursor: "pointer" }} onClick={() => history.push(`/omr-evaluation/${t.Id}`)}>
                  <td>{t.Name}</td>
                  <td>{t.TestDate ? new Date(t.TestDate).toLocaleDateString() : "—"}</td>
                  <td>{t.templateName || "Default"}</td>
                  <td>{t.processedCount}/{t.scanCount}</td>
                  <td>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", background: sc.bg, color: sc.color }}>
                      {t.Status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Button variant="secondary" size="sm" style={{ marginRight: 4, fontSize: "0.75rem" }}
                      onClick={() => history.push(`/omr-evaluation/${t.Id}`)}>
                      Open
                    </Button>
                    <Button variant="primary" size="sm" style={{ fontSize: "0.75rem" }}
                      onClick={() => handleDownloadSheet(t.TemplateId, t.Name)}>
                      Sheet PDF
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Create Test Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton><Modal.Title>Create OMR Test</Modal.Title></Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", fontSize: "0.85rem" }}>Test Name *</label>
            <input className="form-control form-control-sm" value={formName}
              onChange={(e) => setFormName(e.target.value)} placeholder="e.g. Weekly Test 06 KCET" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", fontSize: "0.85rem" }}>Test Date</label>
            <input className="form-control form-control-sm" type="date" value={formDate}
              onChange={(e) => setFormDate(e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", fontSize: "0.85rem" }}>Number of Sets</label>
            <select className="form-control form-control-sm" value={formSets}
              onChange={(e) => setFormSets(Number(e.target.value))}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={4}>4 (A, B, C, D)</option>
            </select>
          </div>
          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
            Default template: KCET 180Q (60 Physics + 60 Chemistry + 60 Mathematics).<br />
            You can customize sections in the test detail page.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleCreate} disabled={creating || !formName.trim()}>
            {creating ? <><Spinner animation="border" size="sm" /> Creating...</> : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OmrEvaluationPage;
